/**
 * `<ThemeEditor>` — pick the workspace's theme, and see what it is made of.
 *
 * Reached by right-click → Settings → Theme. Selecting applies **immediately**
 * rather than on an OK button: colors resolve at render time (D13), so applying
 * a theme costs a repaint, and letting the user see the chart and screener
 * change under the open dialog is the whole point of that design.
 *
 * The selection is workspace data (`UserWorkspace.themeRef`) — this component
 * holds none of it, it dispatches. Whether the dialog is open is ephemeral view
 * state and lives in `App` (D8).
 *
 * **A built-in cannot be edited; a copy of one can.** Editing in place would
 * make "Dark" mean something different for every person who touched it,
 * so *Duplicate* mints an authored theme with a fresh id and the same tokens,
 * and that one is editable. Authored themes are stored per user rather than in
 * the workspace: the contract has `themeRef` and no `themes[]`, and a palette
 * is a property of the person, not of one workspace.
 *
 * ## Except for an admin, who edits the built-ins in place
 *
 * **And it needs no new storage, because the shadowing already exists.**
 * `ThemeProvider` searches authored themes *before* the built-ins, so an
 * authored theme saved under a built-in's own id simply wins. Editing "Dark" as
 * an admin writes exactly that: same id, new tokens, through the endpoint every
 * other authored theme uses.
 *
 * The consequence to be honest about: **this is still per-user.** It shadows the
 * built-in for the admin who edited it, not for everyone — a global palette would
 * need a scope the theme store does not have. So the point of the mode is to
 * *tune* a shipped theme against a real chart instead of guessing hexes in a
 * file, and **Copy** is the other half of it: it puts the tuned tokens on the
 * clipboard as the literal that belongs in `packages/model/src/theme.ts`, which
 * is where a change becomes everyone's.
 *
 * **Reset rewrites rather than deletes.** The store has an upsert and no delete,
 * so putting the shipped tokens back is how an override is undone — the same
 * result on screen, and no endpoint this dialog would otherwise need.
 *
 * `isAdmin` is the server's answer (`/auth/me`), never the client's belief: with
 * no server, no session, or an ordinary account, the mode simply does not exist
 * and the dialog is what it always was.
 *
 * Each token edits with a native colour input. Changes apply on release rather
 * than per pixel of the picker — a theme change repaints every surface, and
 * doing that on every intermediate colour is a lot of work to show a colour
 * nobody stopped on.
 */

import { useEffect, useState } from "react";
import { BUILTIN_THEMES, mintUlid, type Theme, type ThemeTokens } from "@qufolio/model";

import { BRAND } from "../lib/brand-current";
import { allowedBuiltins } from "../lib/brand-themes";

import { fetchMe } from "../api/me";

export interface ThemeEditorProps {
  /** The workspace's current `themeRef`. */
  activeThemeRef?: string;
  /** Apply a theme — dispatched straight into the workspace. */
  onSelect: (themeId: string) => void;
  onClose: () => void;
  /** Themes this user authored. Editable, unlike the built-ins. */
  themes?: readonly Theme[];
  /** Persist an authored theme. Omitted, there is no editing at all. */
  onSaveTheme?: (theme: Theme) => void;
}

/** The handful of tokens that identify a theme at a glance. */
const PREVIEW_TOKENS: (keyof ThemeTokens)[] = [
  "background",
  "surface",
  "grid",
  "text",
  "positive",
  "negative",
  "accent",
];

/**
 * A typed or pasted colour, or `null` if it is not one.
 *
 * Forgiving on the way in, strict on the way out. A hex arrives from a design
 * tool as `#0A3161`, from a spreadsheet as `0a3161`, and from a CSS file as
 * `#abc` — all three mean a colour, and refusing two of them would make the
 * field a spelling test. What leaves is always lowercase six-digit, because that
 * is what every other token in the file is and what `#RRGGBBAA` maths in the
 * renderers assumes.
 */
export function normaliseHex(raw: string): string | null {
  const value = "#" + raw.trim().replace(/^#/, "");
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    return ("#" + [...value.slice(1)].map((c) => c + c).join("")).toLowerCase();
  }
  return /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : null;
}

const MODE_LABEL: Record<Theme["mode"], string> = {
  light: "Light",
  dark: "Dark",
  auto: "Auto",
};

export function ThemeEditor({
  activeThemeRef,
  onSelect,
  onClose,
  themes = [],
  onSaveTheme,
}: ThemeEditorProps) {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let alive = true;
    void fetchMe().then((me) => {
      if (alive) setIsAdmin(me?.isAdmin === true);
    });
    return () => {
      alive = false;
    };
  }, []);

  // **Authored wins, and the list must say so once.** An admin's edit of a
  // built-in is stored under that built-in's id, so concatenating the two lists
  // would show the theme twice — under one React key — and the row the user
  // clicked would not be the one being rendered. Same precedence as
  // `ThemeProvider`, which is what actually paints.
  const all = [
    // **What this brand offers, not everything shipped.** `allowedBuiltins`
    // applies `themes.allow`; a brand's own themes arrive through the same
    // list. Authored themes still override by id, and are never filtered —
    // they are the user's work, not the app's — see `lib/brand-themes.ts`.
    ...BRAND.themes.custom.filter((b) => !themes.some((t) => t.id === b.id)),
    ...allowedBuiltins().filter(
      (b) =>
        !themes.some((t) => t.id === b.id) &&
        !BRAND.themes.custom.some((c) => c.id === b.id),
    ),
    ...themes,
  ];
  // An unknown or absent ref resolves to the default, which is what the app is
  // actually rendering — so the tick has to follow the same rule, or the dialog
  // would show nothing selected while a theme is plainly applied.
  // The first theme *this app offers*, not the first one shipped: a brand may
  // not offer `BUILTIN_THEMES[0]`, and falling back to it would tick a row the
  // list does not contain. The shipped one remains the last resort for the case
  // `resolveBrand` tolerates and `checkProfile` refuses — a brand offering
  // nothing at all — because a dialog with no selection is worse than one
  // showing a theme the brand forgot to allow.
  const active =
    all.find((t) => t.id === activeThemeRef) ?? all[0] ?? BUILTIN_THEMES[0]!;
  // **A brand's own theme is shipped, not authored.** It arrived with the build
  // and belongs to the app, so like a built-in it is duplicated to be edited
  // rather than edited in place — otherwise one user's tweak would silently
  // redefine the brand's palette for the account that made it.
  const shipped =
    BUILTIN_THEMES.find((t) => t.id === active.id) ??
    BRAND.themes.custom.find((t) => t.id === active.id);
  const overridden = shipped !== undefined && themes.some((t) => t.id === active.id);
  const editable =
    onSaveTheme !== undefined && (themes.some((t) => t.id === active.id) || isAdmin);

  /** Copy the active theme into an authored one and switch to it. */
  const duplicate = () => {
    if (!onSaveTheme) return;
    const copy: Theme = {
      ...active,
      id: mintUlid(),
      name: `${active.name} (mine)`,
      tokens: { ...active.tokens },
    };
    onSaveTheme(copy);
    onSelect(copy.id);
  };

  /** Put the shipped tokens back under the same id — the store has no delete. */
  const reset = () => {
    if (!onSaveTheme || !shipped) return;
    onSaveTheme({ ...shipped, tokens: { ...shipped.tokens } });
  };

  /**
   * The active palette as the literal that belongs in `theme.ts`.
   *
   * Because an admin's edit is only ever *their* override, this is the step that
   * makes it everyone's: tune it here against a real chart, copy, paste, ship.
   */
  const copyTokens = () => {
    const body = (Object.keys(active.tokens) as (keyof ThemeTokens)[])
      .map((key) => `    ${key}: "${active.tokens[key]}",`)
      .join("\n");
    void navigator.clipboard?.writeText(`  tokens: {\n${body}\n  },`);
  };

  const setToken = (key: keyof ThemeTokens, value: string) => {
    if (!onSaveTheme || !editable) return;
    onSaveTheme({ ...active, tokens: { ...active.tokens, [key]: value } });
  };

  return (
    <div style={backdrop} onClick={onClose} onContextMenu={(e) => e.preventDefault()}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <header style={header}>
          <strong>Theme</strong>
          <span style={{ marginLeft: "auto", color: "var(--qf-text-muted)", fontSize: "0.72rem" }}>
            Applies immediately
          </span>
          <button onClick={onClose} style={closeButton} title="Close">
            ✕
          </button>
        </header>

        <div style={body}>
          {all.map((theme) => {
            const selected = theme.id === active.id;
            return (
              <button
                key={theme.id}
                onClick={() => onSelect(theme.id)}
                style={{
                  ...themeRow,
                  background: selected ? "var(--qf-selection)" : "transparent",
                  borderColor: selected ? "var(--qf-accent)" : "var(--qf-border)",
                }}
              >
                <span style={{ width: "1rem", color: "var(--qf-accent)" }}>
                  {selected ? "✓" : ""}
                </span>
                <span style={{ minWidth: "8.5rem", textAlign: "left" }}>{theme.name}</span>
                <span style={modeBadge}>{MODE_LABEL[theme.mode]}</span>
                <span style={{ display: "flex", gap: 3, marginLeft: "auto" }}>
                  {PREVIEW_TOKENS.map((key) => (
                    <span
                      key={key}
                      title={`${key}: ${theme.tokens[key]}`}
                      style={{ ...swatch, background: theme.tokens[key] }}
                    />
                  ))}
                </span>
              </button>
            );
          })}

          <div style={tokenSection}>
            <div style={tokenHeading}>
              <span>{active.name} tokens</span>
              {editable ? (
                <>
                  <span style={{ color: "var(--qf-text-muted)" }}>
                    {shipped
                      ? // Said out loud, because "I changed Dark" and "I changed
                        // Dark for everyone" are different things and only one of
                        // them is true.
                        "admin — edits shadow the built-in for you only"
                      : "click a swatch to change it"}
                  </span>
                  <button onClick={copyTokens} style={duplicateButton} title="For theme.ts">
                    Copy tokens
                  </button>
                  {overridden && (
                    <button onClick={reset} style={duplicateButton} title="Back to shipped">
                      Reset
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* Said plainly rather than left as a dead-looking grid. */}
                  <span style={{ color: "var(--qf-text-muted)" }}>built-in, read-only</span>
                  {onSaveTheme && (
                    <button onClick={duplicate} style={duplicateButton}>
                      Duplicate to edit
                    </button>
                  )}
                </>
              )}
            </div>
            <div style={tokenGrid}>
              {(Object.keys(active.tokens) as (keyof ThemeTokens)[]).map((key) => (
                <label
                  // **Keyed on the theme as well as the token, and it must be.**
                  // The input below is uncontrolled, so `defaultValue` is applied
                  // at mount and never again — and this dialog stays open across
                  // a theme switch on purpose. Keyed on the token alone, React
                  // reuses the same input and it keeps showing the *previous*
                  // theme's colour while the hex printed beside it updates: pick
                  // Red White & Blue after Christmas and `positive` reads
                  // `#0a3161` next to a white swatch.
                  //
                  // Not only cosmetic. `onBlur` fires whether or not the value
                  // changed, so focusing that stale input and clicking away
                  // wrote Christmas's white into Red White & Blue — a theme
                  // edited by looking at it.
                  key={`${active.id}:${key}`}
                  style={tokenCell}
                  title={`${key}: ${active.tokens[key]}`}
                >
                  {editable ? (
                    // `onBlur`, not `onChange`: a colour input fires per pixel
                    // of the picker, and each one repaints every surface.
                    <input
                      type="color"
                      defaultValue={active.tokens[key]}
                      // Guarded, so a blur that changed nothing writes nothing.
                      // Belt and braces beside the key above: a no-op save is
                      // still a save, and it would turn a built-in into an
                      // override that merely looks identical.
                      onBlur={(e) => {
                        if (e.target.value.toLowerCase() !== active.tokens[key]?.toLowerCase()) {
                          setToken(key, e.target.value);
                        }
                      }}
                      aria-label={`${key} colour`}
                      style={colorInput}
                    />
                  ) : (
                    <span style={{ ...swatch, background: active.tokens[key] }} />
                  )}
                  <span style={tokenName}>{key}</span>
                  {editable ? (
                    <TokenField
                      name={key}
                      value={active.tokens[key] ?? ""}
                      onCommit={(hex) => setToken(key, hex)}
                    />
                  ) : (
                    <span style={tokenValue}>{active.tokens[key]}</span>
                  )}
                </label>
              ))}
            </div>
            <p style={footnote}>
              One namespace for every surface: the chart and the screener resolve these
              same tokens, so an up candle and a positive cell cannot disagree.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The hex, typed or pasted rather than only picked.
 *
 * The OS colour dialog is how you *find* a colour; this is how you move one you
 * already have — out of a palette, a design tool, or the other half of this
 * theme. Copying a value from one token to another was otherwise a matter of
 * steering a wheel until it matched.
 *
 * **Its own state, not the input's.** The obvious version keeps the text in the
 * DOM and puts a rejected value back with `e.target.value = …` in the blur
 * handler. React undoes that: it tracks the value it last saw on an input and
 * restores it after the event, so the field kept the rubbish that had just been
 * refused. Local state is the honest shape anyway — a half-typed `#0a31` is a
 * state this field is in, and nothing outside it should ever see one.
 *
 * Committed on blur and on Enter, reverted on Escape, and reset by its key when
 * the dialog switches theme underneath it.
 */
function TokenField({
  name,
  value,
  onCommit,
}: {
  name: string;
  value: string;
  onCommit: (hex: string) => void;
}) {
  const [text, setText] = useState(value);

  const commit = () => {
    const hex = normaliseHex(text);
    if (hex && hex !== value.toLowerCase()) onCommit(hex);
    // Anything that is not a colour goes back to what the theme actually says,
    // rather than being saved or left sitting there looking like a value.
    else setText(value);
  };

  return (
    <input
      type="text"
      value={text}
      aria-label={`${name} hex`}
      spellCheck={false}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setText(value);
          e.currentTarget.blur();
        }
      }}
      onBlur={commit}
      style={tokenInput}
    />
  );
}

const backdrop: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.45)",
  // Above the context menu (40), which is what opened this.
  zIndex: 50,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const panel: React.CSSProperties = {
  width: "min(94%, 620px)",
  maxHeight: "85%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 8,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  fontSize: "0.82rem",
  boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
};

const header: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.55rem 0.7rem",
  borderBottom: "1px solid var(--qf-border)",
};

const closeButton: React.CSSProperties = {
  border: "none",
  borderRadius: 4,
  padding: "0.1rem 0.4rem",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  font: "inherit",
};

const body: React.CSSProperties = {
  padding: "0.6rem 0.7rem 0.8rem",
  overflowY: "auto",
  minHeight: 0,
};

const themeRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  width: "100%",
  marginBottom: "0.35rem",
  padding: "0.45rem 0.5rem",
  borderRadius: 6,
  border: "1px solid var(--qf-border)",
  color: "var(--qf-text)",
  cursor: "pointer",
  font: "inherit",
};

const modeBadge: React.CSSProperties = {
  padding: "0 0.35rem",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  color: "var(--qf-text-secondary)",
  fontSize: "0.68rem",
};

const swatch: React.CSSProperties = {
  display: "inline-block",
  width: 14,
  height: 14,
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  flex: "0 0 auto",
};

const tokenSection: React.CSSProperties = {
  marginTop: "0.7rem",
  paddingTop: "0.6rem",
  borderTop: "1px solid var(--qf-border)",
};

const tokenHeading: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "0.4rem",
  fontSize: "0.72rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--qf-text-secondary)",
};

const tokenGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
  gap: "0.25rem 0.6rem",
};

const tokenCell: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  minWidth: 0,
  fontSize: "0.72rem",
};

const tokenName: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "var(--qf-text-secondary)",
};

/**
 * Sized to a six-digit hex and no wider: it sits in a grid of twenty-four cells,
 * and a field that could hold more would push the token names out of line.
 */
const tokenInput: React.CSSProperties = {
  width: "5.2rem",
  font: "inherit",
  fontSize: "0.68rem",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  padding: "1px 3px",
  background: "var(--qf-surface)",
  color: "var(--qf-text-muted)",
  border: "1px solid var(--qf-border)",
  borderRadius: 3,
  outline: "none",
};

const tokenValue: React.CSSProperties = {
  marginLeft: "auto",
  fontFamily: "ui-monospace, monospace",
  color: "var(--qf-text-muted)",
};

const footnote: React.CSSProperties = {
  margin: "0.7rem 0 0",
  fontSize: "0.72rem",
  color: "var(--qf-text-muted)",
};

const duplicateButton: React.CSSProperties = {
  marginLeft: "0.5rem",
  border: "none",
  borderRadius: 4,
  padding: "0.1rem 0.5rem",
  background: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontSize: "0.7rem",
  fontWeight: 600,
  cursor: "pointer",
};

/** Sized to match the read-only swatch, so the grid does not jump when editing. */
const colorInput: React.CSSProperties = {
  width: 18,
  height: 18,
  padding: 0,
  border: "1px solid var(--qf-border)",
  borderRadius: 3,
  background: "transparent",
  cursor: "pointer",
};
